import Dialog from "@components/Dialog"
import { GraphDto } from "@custom-types/graphs"
import { useGraphDocument } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocument"
import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { AppNode } from "@features/builder/graph/types"
import { GraphDetails, GraphNewDto } from "@features/builder/types"
import {
  flattenGraphTagFormValues,
  flattenScenarioDescriptionFormValues,
  formatFormDate,
} from "@features/builder/UpdateMetadata/helpers"
import { GraphLinksFormAtom } from "@features/builder/UpdateMetadata/GraphLinks/atoms"
import MetadataForm from "@features/builder/UpdateMetadata/MetadataForm"
import { MetadataFormValues } from "@features/builder/UpdateMetadata/validation"
import { CircularProgress, Typography } from "@mui/material"
import Button from "@mui/material/Button"
import Grid from "@mui/material/Grid"
import { useReactFlow } from "@xyflow/react"
import { useAtomValue, useSetAtom } from "jotai"
import { enqueueSnackbar } from "notistack"
import { useState } from "react"
import { useDispatch } from "react-redux"
import { graphsApi } from "@store/services/graphsService"
import { useUploadScenarioSourceMutation } from "@store/services/documentService"
import { useEditorNewGraphMutation } from "@store/services/editorService"
import logger from "@utils/logger"
import { updateGraphDocumentData } from "@utils/crdt"
import { applyGraphLinkUpdates } from "@features/builder/UpdateMetadata/GraphLinks/applyGraphLinkUpdates"

const FORM_ID = "update-metadata-form"

type UpdateMetadataDialogProps = {
  open: boolean
  onClose: () => void
  onGraphCreated: (metadata: GraphDto | null) => void
  graph?: GraphDto
}

type GraphSubmitError = {
  data: { detail: { error: string } } | { message: string }
}

const UpdateMetadata = ({ open, onClose, onGraphCreated, graph }: UpdateMetadataDialogProps) => {
  const isEditing = !!graph
  const handleClose = () => {
    setIsSubmitting(false)
    setUserMessage("")
    resetLinks([])
    onClose()
  }
  const [userMessage, setUserMessage] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const graphDocument = useGraphDocument()
  const { data: graphDetails } = useGraphDocumentData()
  const [uploadScenarioSource] = useUploadScenarioSourceMutation()
  const [editorNewGraph] = useEditorNewGraphMutation()
  const dispatch = useDispatch()
  const links = useAtomValue(GraphLinksFormAtom)
  const resetLinks = useSetAtom(GraphLinksFormAtom)
  const { setNodes } = useReactFlow<AppNode>()
  const scenarioData: string[] = isEditing && graphDetails ? JSON.parse(graphDetails).Scenarios : []

  const onSubmit = async (values: MetadataFormValues) => {
    setIsSubmitting(true)
    const scenarioDescriptions = flattenScenarioDescriptionFormValues(
      values.scenario_descriptions ?? [],
    )

    if (graph) {
      if (!graphDocument) {
        logger.error(`Missing CRDT graph document during graph editing of "${graph.key}"`)
        setIsSubmitting(false)
        return
      }
      let uploadedSourceFilePath: string = ""
      let uploadedSourceFileHash: string = ""
      let uploadedScenarios: string[] = []

      if (values.scenario_source instanceof File) {
        // we don't want to upload if there is just a source attached (as a string), only a new file
        const fd = new FormData()

        fd.set("data", values.scenario_source)
        fd.set("graph_id", graph.key)
        fd.set("simulation_frequency", values.simulation_frequency ?? "")
        fd.set("start_date", formatFormDate(values.start_date) ?? "")
        fd.set("end_date", formatFormDate(values.end_date) ?? "")
        fd.set("yearly_rolling_start_month", values.yearly_rolling_start_month?.toString() ?? "1")
        try {
          const uploadResult = await uploadScenarioSource(fd).unwrap()

          // TODO ms: add scenario_version here once BE method is fixed
          values.scenario_source = uploadResult.path
          values.scenario_version = uploadResult.scenario_hash
          values.scenarios = uploadResult.scenarios
          uploadedSourceFilePath = uploadResult.path
          uploadedSourceFileHash = uploadResult.scenario_hash
          uploadedScenarios = uploadResult.scenarios
        } catch (exceptionVar) {
          enqueueSnackbar("Failed to upload scenario source file.", {
            variant: "error",
            persist: true,
          })
          setIsSubmitting(false)
          return
        }
      }

      const valuesToUpdate: Partial<GraphDetails> = {
        YearlyRollingStartMonth:
          typeof values.yearly_rolling_start_month === "number"
            ? Number(values.yearly_rolling_start_month).toString()
            : "1",
        RollingHorizon:
          typeof values.rolling_duration === "number"
            ? Number(values.rolling_duration).toString()
            : "",
        SimulationFrequency: values.simulation_frequency ?? "",
        EndDate: formatFormDate(values.end_date),
        StartDate: formatFormDate(values.start_date),
        Product: values.product_key,
        description: values.description ?? "",
        Graph: values.name,
        GraphTags: flattenGraphTagFormValues(values.tags ?? []),
        Overview: values.overview ?? "",
        ScenarioDescription: scenarioDescriptions,
      }

      if (uploadedSourceFileHash) {
        valuesToUpdate.ScenarioSource = uploadedSourceFilePath
        valuesToUpdate.ScenarioVersion = uploadedSourceFileHash
        valuesToUpdate.Scenarios = uploadedScenarios
      }

      const data: GraphDetails = JSON.parse(
        graphDocument.getMap("graph").get("data")?.toString() || "",
      )
      const newData = Object.assign(data, valuesToUpdate)
      updateGraphDocumentData(graphDocument, newData)

      setNodes((currNodes) => applyGraphLinkUpdates(links, currNodes))

      // Invalidate scenario-related queries if scenario file was uploaded
      if (uploadedSourceFileHash) {
        dispatch(graphsApi.util.invalidateTags(["ScenarioSourceMnemonics", "MnemonicsData"]))
      }

      handleClose()
    } else {
      if (values.scenario_source instanceof File) {
        const fd = new FormData()
        fd.set("data", values.scenario_source as unknown as File)
        fd.set("new", "true")
        fd.set("graph_id", values.product_key + "#" + values.name + "_0.0.0")
        fd.set("simulation_frequency", values.simulation_frequency ?? "")
        fd.set("start_date", formatFormDate(values.start_date) ?? "")
        fd.set("end_date", formatFormDate(values.end_date) ?? "")
        fd.set("yearly_rolling_start_month", values.yearly_rolling_start_month?.toString() ?? "1")
        const uploadResult = await uploadScenarioSource(fd)
        if (!("data" in uploadResult) || !uploadResult.data) return
        // TODO ms: add scenario_version here once BE method is fixed
        values.scenario_source = uploadResult.data.path
        values.scenario_version = uploadResult.data.scenario_hash
        values.scenarios = uploadResult.data.scenarios
      }
      // TR: no side effect on start date/end date for timezone #515317
      try {
        const newGraphPayload: GraphNewDto = {
          name: values.name,
          description: values.description ?? "",
          product_key: values.product_key,
          start_date: formatFormDate(values.start_date),
          end_date: formatFormDate(values.end_date),
          rolling_duration:
            typeof values.rolling_duration === "number"
              ? Number(values.rolling_duration).toString()
              : "",
          simulation_frequency: values.simulation_frequency ?? "",
          yearly_rolling_start_month:
            typeof values.yearly_rolling_start_month === "number"
              ? Number(values.yearly_rolling_start_month).toString()
              : "1",
          scenario_source: (values.scenario_source as string) ?? "",
          scenario_version: values.scenario_version,
          owner_group: values.owner_group ?? "",
          graph_tags: flattenGraphTagFormValues(values.tags ?? []),
          overview: values.overview ?? "",
          scenario_descriptions: scenarioDescriptions,
        }
        const newGraph = await editorNewGraph(newGraphPayload).unwrap()
        onGraphCreated(newGraph)
      } catch (exceptionVar) {
        const exception = exceptionVar as GraphSubmitError
        if ("message" in exception.data) {
          logger.error(exception.data.message)
          setUserMessage(exception.data?.message)
        }

        if ("detail" in exception.data) {
          logger.error(exception.data.detail?.error)
          setUserMessage(exception.data?.detail?.error)
        }

        setIsSubmitting(false)
      }
    }

    setIsSubmitting(false)
  }

  if (isEditing && !graphDetails) {
    return (
      <Grid container justifyContent="center" sx={{ p: 1 }}>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  return (
    <Dialog
      open={open}
      sx={{ "& .MuiDialog-paper": { maxWidth: 900, width: "100%" } }}
      onClose={handleClose}
      title={graph ? "Edit Graph Metadata" : "Create Graph Metadata"}
      actionsComponent={
        <>
          <Button onClick={handleClose} variant={"contained"}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            type="submit"
            form={FORM_ID}
            disabled={isSubmitting}
            startIcon={isSubmitting && <CircularProgress size={14} />}
          >
            {graph ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <Grid container>
        <Grid item xs={12} sx={{ minWidth: 450 }}>
          <Typography variant="h6" color="red">
            {userMessage}
          </Typography>
          <MetadataForm
            id={FORM_ID}
            graphDetails={isEditing ? graphDetails : undefined}
            graphKey={graph?.key}
            scenarios={graph ? scenarioData || [] : []}
            onSubmit={onSubmit}
          />
        </Grid>
      </Grid>
    </Dialog>
  )
}

export default UpdateMetadata
